import React, { useEffect, useState } from 'react';
import { Carousel as BootstrapCarousel, Card, Container, Alert } from 'react-bootstrap';

// hooks
import { api, MasterTableItem } from 'landing-page/helpers';
import { useApi } from 'landing-page/hooks/useApi';

// styles
import styles from './Carousel.module.scss';

interface CarouselProps {
  items?: MasterTableItem[];
  autoFetch?: boolean;
  carouselType?: string;
}

// Loading placeholder component
const CarouselPlaceholder: React.FC = () => (
  <Container className='py-10'>
    <Card className="border-0 shadow-sm max-height-500 bg-primary">
      <div className="row d-flex align-items-center justify-content-between " style={{ height: '400px' }}>
        <div className="col-md-6">
          <Card.Body className="d-flex flex-column justify-content-center">
            <div className="mb-4 placeholder-glow">
              <div className="placeholder bg-white bg-opacity-20 rounded" style={{ height: '60px', width: '80%' }}></div>
            </div>
            <div className="mb-4 placeholder-glow">
              <div className="placeholder bg-white bg-opacity-20 rounded" style={{ height: '20px', width: '100%', marginBottom: '10px' }}></div>
              <div className="placeholder bg-white bg-opacity-20 rounded" style={{ height: '20px', width: '90%', marginBottom: '10px' }}></div>
              <div className="placeholder bg-white bg-opacity-20 rounded" style={{ height: '20px', width: '70%' }}></div>
            </div>
          </Card.Body>
        </div>
        <div className="col-md-6 d-flex justify-content-center align-items-center placeholder-glow">
          <div className="placeholder bg-white bg-opacity-20 rounded" style={{ height: '300px', width: '80%' }}></div>
        </div>
      </div>
    </Card>
  </Container>
);

// Error component
const CarouselError: React.FC<{ error: string | undefined; onRetry: () => void }> = ({ error, onRetry }) => (
  <Container className='py-10' style={{ minHeight: '400px' }}>
    <Alert variant="warning" className="text-center">
      <Alert.Heading>Oops! Something went wrong</Alert.Heading>
      <p>{error}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        Try Again
      </button>
    </Alert>
  </Container>
);

const Carousel: React.FC<CarouselProps> = ({ 
  items: propItems = [], 
  autoFetch = true, 
  carouselType = 'header_1' 
}) => {

  const [carouselItems, setCarouselItems] = useState<MasterTableItem[]>(propItems);
  
  const { data, loading, error, execute: fetchCarouselData } = useApi(api.getMasterTable.bind(api));

  useEffect(() => {
    if (autoFetch && propItems.length === 0) {
      fetchCarouselData({ tipe: carouselType });
    } else if (propItems.length > 0) {
      setCarouselItems(propItems);
    }
  }, [autoFetch, propItems.length, carouselType]); // Remove fetchCarouselData from dependencies

  // Update carousel items when API data is received
  useEffect(() => {
    if (data?.data?.data && Array.isArray(data.data.data)) {
      const filteredItems = data.data.data.filter((item: MasterTableItem) => 
        item.is_active === 1 || item.is_active === '1'
      );
      setCarouselItems(filteredItems || []);
    }
  }, [data]);

  // Handle retry
  const handleRetry = () => {
    fetchCarouselData({ tipe: carouselType });
  };

  // Show loading placeholder
  if (loading && carouselItems.length === 0) {
    return <CarouselPlaceholder />;
  }

  // Show error state
  if (error && carouselItems.length === 0) {
    return <CarouselError error={error?.message} onRetry={handleRetry} />;
  }

  // Show empty state
  if (carouselItems.length === 0) {
    return <CarouselPlaceholder />;
  }

  return (
    <Container className='py-10' style={{ minHeight: '400px' }}>
      <BootstrapCarousel 
        className={styles.carouselWrapper} 
        indicators={carouselItems.length > 1}
        controls={carouselItems.length > 1}
        interval={5000}
        pause="hover"
      >
        {carouselItems.map((item, index) => (
          <BootstrapCarousel.Item key={item.id || index} className={styles.carouselItem}>
            <Card className="border-0 shadow-sm max-height-500" style={{ backgroundColor: '#F95E2F', borderRadius: '20px' }}>
              <div className="row g-0 d-flex align-items-center justify-content-between" style={{ height: '400px' }}>
                <div className="col-md-7">
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <Card.Title as="h2" className="display-5 text-white fw-bold mb-4">
                      {item.title_id}
                    </Card.Title>
                    <Card.Text className="fs-4 text-white mb-4 text-start">
                      {item.deskripsi_id}
                    </Card.Text>
                  </Card.Body>
                </div>
                <div className="col-md-5 d-flex justify-content-center align-items-center mt-4 mt-md-0">
                  <Card.Img 
                    src={item.image} 
                    alt={item.title_id}
                    className={styles.carouselImage}
                    style={{
                      maxHeight: '400px',
                      width: '100%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      // Fallback for broken images
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </Card>
          </BootstrapCarousel.Item>
        ))}
      </BootstrapCarousel>
    </Container>
  );
};

export default Carousel;